import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  shift,
  useFloating,
} from '@floating-ui/react';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { cn } from '#/utils/cn';

import { Button } from '../ui/Button';

interface DefinitionTooltipProps {
  termSlug: string;
  children: ReactNode;
  className?: string;

  // Optional call-site overrides for the glossary content. `definition` accepts
  // a ReactNode so a call site can author rich markup (e.g. a fee schedule);
  // when omitted, the hover copy comes from the page's glossary term.
  definition?: ReactNode;
  enhancedDefinition?: string;
}

export function DefinitionTooltip({
  termSlug,
  children,
  definition,
  enhancedDefinition,
  className,
}: DefinitionTooltipProps) {
  const [visible, setVisible] = useState(false);
  const { open, registerTerm, unregisterTerm, termsBySlug } =
    useDefinitionDrawer();
  const popoverId = useId();
  const arrowRef = useRef(null);
  const skipNextFocus = useRef(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    middleware: [arrow({ element: arrowRef }), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  // Glossary content for this term, with call-site overrides winning.
  const term = termsBySlug.get(termSlug);
  const resolvedDefinition = definition ?? term?.tooltip ?? null;
  const resolvedEnhanced =
    enhancedDefinition !== undefined
      ? enhancedDefinition
      : (term?.expandedDefinition ?? null);

  // The drawer affordance appears only once a term has expanded copy authored.
  const hasDrawer = Boolean(resolvedEnhanced);
  const hasPopover = resolvedDefinition != null || hasDrawer;

  useEffect(() => {
    if (!hasDrawer) {
      return;
    }
    registerTerm(termSlug);
    return () => unregisterTerm(termSlug);
  }, [hasDrawer, termSlug, registerTerm, unregisterTerm]);

  function handleBlur(e: React.FocusEvent) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setVisible(false);
    }
  }

  function handleClick() {
    if (hasDrawer) {
      setVisible(false);
      skipNextFocus.current = true;
      open(termSlug);
    }
  }

  return (
    <span
      className={cn('inline-block', className)}
      onMouseLeave={() => setVisible(false)}
      onBlur={handleBlur}
    >
      <button
        ref={refs.setReference}
        type="button"
        data-track="definition_tooltip"
        data-track-context="glossary_tooltip"
        data-track-term-slug={termSlug}
        aria-expanded={visible && hasPopover}
        aria-controls={visible && hasPopover ? popoverId : undefined}
        onMouseEnter={() => setVisible(true)}
        onFocus={() => {
          if (skipNextFocus.current) {
            skipNextFocus.current = false;
            return;
          }
          setVisible(true);
        }}
        onClick={handleClick}
        className="decoration-blue-60 cursor-help underline decoration-dotted decoration-2 underline-offset-2"
      >
        {children}
      </button>

      {visible && hasPopover && (
        <span
          ref={refs.setFloating}
          id={popoverId}
          role="tooltip"
          style={floatingStyles}
          className="bg-tooltip-bg text-tooltip-fg text-static-14 z-10 max-w-[280px] min-w-[200px] rounded p-12 shadow-md"
        >
          {resolvedDefinition && (
            <span className="block">{resolvedDefinition}</span>
          )}
          {hasDrawer && (
            <Button
              variant="link"
              suffixIcon="arrow-right"
              size="sm"
              onClick={handleClick}
              data-track="tooltip_learn_more"
              data-track-context="glossary_tooltip"
              data-track-term-slug={termSlug}
            >
              Click to learn more
            </Button>
          )}
          <FloatingArrow
            ref={arrowRef}
            fill="var(--color-tooltip-bg)"
            context={context}
          />
        </span>
      )}
    </span>
  );
}
